import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Save, Trash2, Wand2 } from "lucide-react";
import { adminApi, api as publicApi } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import { useDebouncedAsync } from "../../hooks/useDebouncedAsync";

const CATEGORIES = ["Staff", "Habbo Club", "Event", "Jubiläum", "Account", "Achievement", "Sonstige"];

const emptyBadge = { code: "", name: "", category: "Event", description: "", year: new Date().getFullYear(), order: 100 };

const BadgesPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyBadge);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setBadges(await a.listBadges()); } finally { setLoading(false); }
  };
  useEffect(() => { load();  }, []);

  const update = (id, field, val) =>
    setBadges((prev) => prev.map((x) => (x.id === id ? { ...x, [field]: val } : x)));

  const onCreate = async () => {
    if (!form.code.trim() || !form.name.trim()) return;
    setCreating(true);
    try {
      await a.createBadge({ ...form, year: parseInt(form.year || 0, 10) });
      setForm(emptyBadge);
      toast({ title: "Badge angelegt" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setCreating(false); }
  };

  const onSave = async (b) => {
    setSavingId(b.id);
    try {
      await a.updateBadge(b.id, {
        code: b.code, name: b.name, category: b.category,
        description: b.description || "", year: parseInt(b.year || 0, 10), order: b.order || 0,
      });
      toast({ title: "Gespeichert" });
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setSavingId(null); }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Badge "${b.code}" loeschen?`)) return;
    try { await a.deleteBadge(b.id); toast({ title: "Geloescht" }); await load(); }
    catch { toast({ title: "Fehler", variant: "destructive" }); }
  };

  const badgeImg = (code) => `https://images.habbo.com/c_images/album1584/${code}.gif`;

  const autoLookup = async (code) => {
    if (!code) return;
    try {
      const data = await publicApi.badgeGamedata(code);
      if (data?.found) {
        setForm((f) => ({
          ...f,
          name: f.name?.trim() ? f.name : data.name || f.name,
          description: f.description?.trim() ? f.description : data.description || f.description,
        }));
        toast({ title: "Gamedata gefunden", description: data.name });
      } else {
        toast({ title: "Kein Eintrag", description: `Code ${code} ist nicht in den deutschen Gamedata-Texten.`, variant: "destructive" });
      }
    } catch { /* ignore */ }
  };
  const debouncedAutoLookup = useDebouncedAsync(autoLookup, 350);

  return (
    <div>
      <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5">
        <div className="text-sm font-semibold text-white">Neues Badge anlegen</div>
        <div className="mt-1 text-[11px] text-zinc-500">Tipp: Code eingeben und Tab drücken – Name &amp; Beschreibung werden automatisch aus den Habbo-Gamedata gefüllt.</div>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[90px_1fr_1.5fr_1fr_auto_auto]">
          <Input
            data-testid="new-badge-code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
            onBlur={(e) => debouncedAutoLookup(e.target.value.trim().toUpperCase())}
            placeholder="Code"
            className="bg-[#10141b] border-white/10 text-zinc-200"
          />
          <Input data-testid="new-badge-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <Input data-testid="new-badge-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Beschreibung" className="bg-[#10141b] border-white/10 text-zinc-200" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-md border border-white/10 bg-[#10141b] px-3 py-2 text-sm text-zinc-200">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button
            type="button"
            data-testid="new-badge-autofill"
            onClick={() => autoLookup(form.code.trim().toUpperCase())}
            className="btn-ghost rounded-lg px-3 text-sm"
            title="Aus Habbo Gamedata befüllen"
          ><Wand2 className="mr-1 h-4 w-4" /> Auto</Button>
          <Button onClick={onCreate} disabled={creating} className="btn-primary rounded-lg px-4 text-sm">
            {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} Anlegen
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10 bg-[#0f141b]">
          <div className="grid grid-cols-[60px_90px_1.2fr_1.6fr_120px_120px] gap-3 border-b border-white/10 bg-[#0a0d12] px-4 py-2 text-[11px] uppercase tracking-wider text-zinc-500">
            <div>Icon</div><div>Code</div><div>Name</div><div>Beschreibung</div><div>Kategorie</div><div className="text-right">Aktionen</div>
          </div>
          {badges.map((b) => (
            <div key={b.id} className={`grid grid-cols-[60px_90px_1.2fr_1.6fr_120px_120px] items-center gap-3 border-b border-white/5 px-4 py-2 last:border-b-0 ${b.is_new ? "bg-emerald-500/5" : ""}`}>
              <div className="relative flex justify-center">
                {b.is_new && (
                  <span
                    className="pointer-events-none absolute -top-2 -right-1 z-10 inline-flex items-center gap-0.5 rounded-md border border-emerald-400/60 bg-gradient-to-br from-emerald-500 to-teal-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white shadow shadow-emerald-500/40"
                    title={`Neu hinzugefuegt: ${b.added_at || ""}`}
                    data-testid={`badge-new-${b.id}`}
                  >
                    Neu
                  </span>
                )}
                <img src={badgeImg(b.code)} alt={b.code} className="hb-pixel-img h-10 w-10" />
              </div>
              <Input value={b.code} onChange={(e) => update(b.id, "code", e.target.value.toUpperCase())} className="bg-[#10141b] border-white/10 text-zinc-200" />
              <Input value={b.name} onChange={(e) => update(b.id, "name", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" />
              <Input value={b.description || ""} onChange={(e) => update(b.id, "description", e.target.value)} className="bg-[#10141b] border-white/10 text-zinc-200" />
              <select value={b.category} onChange={(e) => update(b.id, "category", e.target.value)} className="rounded-md border border-white/10 bg-[#10141b] px-2 py-1.5 text-sm text-zinc-200">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="flex items-center justify-end gap-1">
                <Button onClick={() => onSave(b)} disabled={savingId === b.id} className="btn-primary rounded-lg px-2 py-1 text-xs">
                  {savingId === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                </Button>
                <Button onClick={() => onDelete(b)} className="btn-ghost rounded-lg px-2 py-1 text-xs text-rose-300 hover:border-rose-500/40">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BadgesPanel;
